import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import {
  COMPANY_KIND_LABELS,
  COMPANY_KINDS,
  TRADES,
  WEEKDAY_LABELS,
  WEEKDAYS,
  type CertificationDto,
  type CompanyDetailDto,
  type CompanyDto,
  type CompanyKind,
  type DirectoryPersonDto,
  type DirectoryUserDto,
  type DuplicateDto,
  type OpeningHours,
  type Trade,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";

export const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");

/** Alle lijsten en panelen van het adresboek opnieuw laden. */
export function useDirectoryRefresh() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["companies"] }),
      qc.invalidateQueries({ queryKey: ["company"] }),
      qc.invalidateQueries({ queryKey: ["people"] }),
      qc.invalidateQueries({ queryKey: ["person"] }),
      qc.invalidateQueries({ queryKey: ["directory-users"] }),
    ]);
}

/** Na een korte pauze zoeken of het record al bestaat (KvK, mail, telefoon, naam). */
export function useDuplicates(type: "company" | "person", fields: Record<string, string>, exclude?: string) {
  const key = JSON.stringify(fields);
  const [query, setQuery] = useState(key);
  useEffect(() => {
    const t = setTimeout(() => setQuery(key), 400);
    return () => clearTimeout(t);
  }, [key]);
  const params = new URLSearchParams({ type, ...(exclude ? { exclude } : {}) });
  for (const [k, v] of Object.entries(JSON.parse(query) as Record<string, string>)) if (v.trim()) params.set(k, v);
  const any = [...params.keys()].some((k) => k !== "type" && k !== "exclude");
  return useQuery({
    queryKey: ["directory-duplicates", params.toString()],
    queryFn: () => api<DuplicateDto[]>(`/directory/duplicates?${params}`),
    enabled: any,
  }).data;
}

const MATCH_LABELS: Record<DuplicateDto["match"], string> = {
  kvk: "zelfde KvK-nummer",
  email: "zelfde mailadres",
  phone: "zelfde telefoonnummer",
  name: "lijkt op de naam",
};

export function DuplicateHint({ hits, onUse }: { hits: DuplicateDto[] | undefined; onUse: (d: DuplicateDto) => void }) {
  if (!hits?.length) return null;
  return (
    <div className="alert info dir-dup" role="status">
      {hits.map((d) => (
        <div key={d.id} className="dir-dupitem">
          <span className="grow">
            Bedoel je <strong>{d.name}</strong>?{" "}
            <span className="muted small">
              {MATCH_LABELS[d.match]}
              {d.detail ? ` · ${d.detail}` : ""}
            </span>
          </span>
          <button type="button" className="btn small" onClick={() => onUse(d)}>
            Die gebruiken
          </button>
        </div>
      ))}
      <p className="small muted">Is het een ander bedrijf of andere persoon? Sla dan gewoon op.</p>
    </div>
  );
}

// ---------- bedrijf ----------

interface CompanyForm {
  name: string;
  kinds: CompanyKind[];
  trades: Trade[];
  kvk: string;
  address: string;
  postalCode: string;
  city: string;
  email: string;
  phone: string;
  emergencyPhone: string;
  website: string;
  notes: string;
  openingHours: OpeningHours;
}
const companyForm = (c?: CompanyDto, kind: CompanyKind = "supplier"): CompanyForm => ({
  name: c?.name ?? "",
  kinds: c?.kinds ?? [kind],
  trades: c?.trades ?? [],
  kvk: c?.kvk ?? "",
  address: c?.address ?? "",
  postalCode: c?.postalCode ?? "",
  city: c?.city ?? "",
  email: c?.email ?? "",
  phone: c?.phone ?? "",
  emergencyPhone: c?.emergencyPhone ?? "",
  website: c?.website ?? "",
  notes: c?.notes ?? "",
  openingHours: c?.openingHours ?? {},
});

function toggle<T>(list: T[], v: T): T[] {
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}

/** Nieuw bedrijf of bedrijf bewerken, met controle op dubbelen en openingstijden per dag. */
export function CompanyDialog({
  company,
  onClose,
  onSaved,
}: {
  company?: CompanyDto;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const toast = useToast();
  const refresh = useDirectoryRefresh();
  const [f, setF] = useState<CompanyForm>(() => companyForm(company));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof CompanyForm) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const dups = useDuplicates("company", { name: f.name, kvk: f.kvk, email: f.email, phone: f.phone }, company?.id);
  const setDay = (d: (typeof WEEKDAYS)[number], part: "from" | "to", v: string) => {
    const cur = f.openingHours[d] ?? { from: "", to: "" };
    const next = { ...cur, [part]: v };
    setF({ ...f, openingHours: { ...f.openingHours, [d]: next.from || next.to ? next : null } });
  };
  const copyMonday = () => {
    const ma = f.openingHours.ma;
    if (!ma) return;
    setF({ ...f, openingHours: { ...f.openingHours, di: ma, wo: ma, do: ma, vr: ma } });
  };

  async function save(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    const hours = Object.fromEntries(
      Object.entries(f.openingHours).filter(([, h]) => h && h.from && h.to),
    ) as OpeningHours;
    const body = { ...f, openingHours: Object.keys(hours).length ? hours : null };
    try {
      const c = company
        ? await api<CompanyDetailDto>(`/companies/${company.id}`, { method: "PATCH", body })
        : await api<CompanyDetailDto>("/companies", { method: "POST", body });
      await refresh();
      toast(company ? "Bedrijf opgeslagen" : `${c.name} toegevoegd`);
      onSaved(c.id);
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={company ? `${company.name} bewerken` : "Nieuw bedrijf"}
      onClose={onClose}
      busy={busy}
      wide
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => save()}
            disabled={busy || !f.name.trim() || !f.kinds.length}
          >
            {company ? "Opslaan" : "Toevoegen"}
          </button>
        </>
      }
    >
      <form onSubmit={save} className="dir-form">
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <label className="field">
          <span className="label">Naam *</span>
          <input value={f.name} onChange={set("name")} required placeholder="bijv. Liftservice Zuid" />
        </label>
        {!company?.isInternal && (
          <DuplicateHint
            hits={dups}
            onUse={(d) => {
              onSaved(d.id);
            }}
          />
        )}
        <fieldset className="field dir-checks">
          <legend className="label">Soort *</legend>
          {COMPANY_KINDS.map((k) => (
            <label key={k} className="dir-check">
              <input
                type="checkbox"
                checked={f.kinds.includes(k)}
                disabled={company?.isInternal}
                onChange={() => setF({ ...f, kinds: toggle(f.kinds, k) })}
              />{" "}
              {COMPANY_KIND_LABELS[k]}
            </label>
          ))}
        </fieldset>
        <fieldset className="field dir-checks">
          <legend className="label">Vakgebied</legend>
          {TRADES.map((t) => (
            <label key={t} className="dir-check">
              <input
                type="checkbox"
                checked={f.trades.includes(t)}
                onChange={() => setF({ ...f, trades: toggle(f.trades, t) })}
              />{" "}
              {t}
            </label>
          ))}
        </fieldset>
        <div className="grid3">
          <label className="field">
            <span className="label">Telefoon</span>
            <input value={f.phone} onChange={set("phone")} inputMode="tel" autoComplete="off" />
          </label>
          <label className="field">
            <span className="label">Storingsnummer (24 uur)</span>
            <input value={f.emergencyPhone} onChange={set("emergencyPhone")} inputMode="tel" autoComplete="off" />
          </label>
          <label className="field">
            <span className="label">E-mailadres</span>
            <input value={f.email} onChange={set("email")} type="email" autoComplete="off" />
          </label>
        </div>
        <div className="grid3">
          <label className="field">
            <span className="label">Adres</span>
            <input value={f.address} onChange={set("address")} />
          </label>
          <label className="field">
            <span className="label">Postcode</span>
            <input value={f.postalCode} onChange={set("postalCode")} />
          </label>
          <label className="field">
            <span className="label">Plaats</span>
            <input value={f.city} onChange={set("city")} />
          </label>
        </div>
        <div className="grid2">
          <label className="field">
            <span className="label">KvK-nummer</span>
            <input value={f.kvk} onChange={set("kvk")} inputMode="numeric" maxLength={8} placeholder="8 cijfers" />
          </label>
          <label className="field">
            <span className="label">Website</span>
            <input value={f.website} onChange={set("website")} />
          </label>
        </div>
        <fieldset className="field dir-hours">
          <legend className="label">Openingstijden</legend>
          {WEEKDAYS.map((d) => (
            <div key={d} className="dir-hourrow">
              <span className="dir-day">{WEEKDAY_LABELS[d]}</span>
              <input
                type="time"
                aria-label={`${WEEKDAY_LABELS[d]} van`}
                value={f.openingHours[d]?.from ?? ""}
                onChange={(e) => setDay(d, "from", e.target.value)}
              />
              <span aria-hidden="true">–</span>
              <input
                type="time"
                aria-label={`${WEEKDAY_LABELS[d]} tot`}
                value={f.openingHours[d]?.to ?? ""}
                onChange={(e) => setDay(d, "to", e.target.value)}
              />
              {d === "ma" && (
                <button type="button" className="btn small ghost" onClick={copyMonday} disabled={!f.openingHours.ma}>
                  Ook di–vr
                </button>
              )}
            </div>
          ))}
          <p className="hint">Leeg = gesloten. Het storingsnummer geldt buiten deze tijden.</p>
        </fieldset>
        <label className="field">
          <span className="label">Notities</span>
          <textarea value={f.notes} onChange={set("notes")} rows={3} />
        </label>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

// ---------- erkenning ----------

export function CertDialog({
  companyId,
  cert,
  onClose,
}: {
  companyId: string;
  cert?: CertificationDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const refresh = useDirectoryRefresh();
  const [name, setName] = useState(cert?.name ?? "");
  const [number, setNumber] = useState(cert?.number ?? "");
  const [validUntil, setValidUntil] = useState(cert?.validUntil ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
      toast(ok);
      onClose();
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  }
  const body = { name, number, validUntil: validUntil || null };
  const save = (e?: FormEvent) => {
    e?.preventDefault();
    void run(
      () =>
        cert
          ? api(`/certifications/${cert.id}`, { method: "PATCH", body })
          : api(`/companies/${companyId}/certifications`, { method: "POST", body }),
      "Erkenning opgeslagen",
    );
  };
  return (
    <Modal
      title={cert ? "Erkenning bewerken" : "Nieuwe erkenning"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          {cert && (
            <button
              type="button"
              className="btn ghost danger"
              disabled={busy}
              onClick={() =>
                void run(() => api(`/certifications/${cert.id}`, { method: "DELETE" }), "Erkenning verwijderd")
              }
            >
              Verwijderen
            </button>
          )}
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => save()} disabled={busy || !name.trim()}>
            Opslaan
          </button>
        </>
      }
    >
      <form onSubmit={save}>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <label className="field">
          <span className="label">Naam *</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="bijv. BMI-erkenning, VCA, F-gassen"
          />
        </label>
        <div className="grid2">
          <label className="field">
            <span className="label">Nummer</span>
            <input value={number} onChange={(e) => setNumber(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Geldig tot</span>
            <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </label>
        </div>
        <p className="hint">60 dagen voor de einddatum krijg je een waarschuwing.</p>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

// ---------- samenvoegen ----------

export function MergeDialog({
  company,
  onClose,
  onMerged,
}: {
  company: CompanyDto;
  onClose: () => void;
  onMerged: (id: string) => void;
}) {
  const toast = useToast();
  const refresh = useDirectoryRefresh();
  const list = useQuery({ queryKey: ["companies", "active"], queryFn: () => api<CompanyDto[]>("/companies") });
  const [intoId, setIntoId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const others = (list.data ?? []).filter((c) => c.id !== company.id);
  async function merge() {
    setBusy(true);
    setError(null);
    try {
      const c = await api<CompanyDetailDto>(`/companies/${company.id}/merge`, { method: "POST", body: { intoId } });
      await refresh();
      toast(`Samengevoegd met ${c.name}`);
      onMerged(c.id);
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  }
  return (
    <Modal
      title={`${company.name} samenvoegen`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void merge()} disabled={busy || !intoId}>
            Samenvoegen
          </button>
        </>
      }
    >
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      <label className="field">
        <span className="label">Samenvoegen met</span>
        <select value={intoId} onChange={(e) => setIntoId(e.target.value)}>
          <option value="">Kies het bedrijf dat blijft</option>
          {others.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.city ? ` · ${c.city}` : ""}
            </option>
          ))}
        </select>
      </label>
      <p className="hint">
        Personen en erkenningen van {company.name} gaan naar het gekozen bedrijf. Lege gegevens daar worden aangevuld.
        Daarna verdwijnt {company.name} uit het adresboek. Dit kun je niet ongedaan maken.
      </p>
    </Modal>
  );
}

// ---------- persoon ----------

interface PersonForm {
  name: string;
  companyId: string;
  jobTitle: string;
  email: string;
  phone: string;
  mobile: string;
  availability: string;
  notes: string;
  userId: string;
}

/** Nieuwe persoon of persoon bewerken. Bij Eigen dienst kies je een gebruiker; naam en mail komen uit het account. */
export function PersonDialog({
  person,
  companyId,
  initialName,
  onClose,
  onSaved,
}: {
  person?: DirectoryPersonDto;
  companyId?: string;
  /** Naam die al getypt is in een zoekkiezer. */
  initialName?: string;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const toast = useToast();
  const refresh = useDirectoryRefresh();
  const cos = useQuery({ queryKey: ["companies", "active"], queryFn: () => api<CompanyDto[]>("/companies") });
  const [f, setF] = useState<PersonForm>({
    name: person?.name ?? initialName ?? "",
    companyId: person?.company?.id ?? companyId ?? "",
    jobTitle: person?.jobTitle ?? "",
    email: person?.email ?? "",
    phone: person?.phone ?? "",
    mobile: person?.mobile ?? "",
    availability: person?.availability ?? "",
    notes: person?.notes ?? "",
    userId: "",
  });
  const internal = cos.data?.find((c) => c.id === f.companyId)?.isInternal ?? false;
  const team = useQuery({
    queryKey: ["directory-users"],
    queryFn: () => api<DirectoryUserDto[]>("/directory/users"),
    enabled: internal && !person,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof PersonForm) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const dups = useDuplicates("person", { name: f.name, email: f.email, phone: f.mobile || f.phone }, person?.id);

  async function save(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    const { userId, ...rest } = f;
    const body = { ...rest, companyId: f.companyId || null, ...(userId && !person ? { userId } : {}) };
    try {
      const p = person
        ? await api<DirectoryPersonDto>(`/people/${person.id}`, { method: "PATCH", body })
        : await api<DirectoryPersonDto>("/people", { method: "POST", body });
      await refresh();
      toast(person ? "Persoon opgeslagen" : `${p.name} toegevoegd`);
      onSaved(p.id);
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={person ? `${person.name} bewerken` : "Nieuwe persoon"}
      onClose={onClose}
      busy={busy}
      wide
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => save()}
            disabled={busy || (!f.name.trim() && !f.userId)}
          >
            {person ? "Opslaan" : "Toevoegen"}
          </button>
        </>
      }
    >
      <form onSubmit={save} className="dir-form">
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <label className="field">
          <span className="label">Bedrijf</span>
          <select value={f.companyId} onChange={set("companyId")}>
            <option value="">Zonder bedrijf</option>
            {(cos.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        {internal && !person && (
          <label className="field">
            <span className="label">Gebruiker</span>
            <select
              value={f.userId}
              onChange={(e) => {
                const u = team.data?.find((x) => x.id === e.target.value);
                setF({ ...f, userId: e.target.value, name: u ? (u.name ?? "") : f.name, email: u ? u.email : f.email });
              }}
            >
              <option value="">Geen account (alleen contactgegevens)</option>
              {(team.data ?? []).map((u) => (
                <option key={u.id} value={u.id} disabled={!!u.personId}>
                  {u.name ?? u.email}
                  {u.personId ? " · staat al in het adresboek" : ""}
                </option>
              ))}
            </select>
            <span className="hint">
              Een monteur is zo gebruiker en contactpersoon tegelijk, en staat maar één keer in het adresboek.
            </span>
          </label>
        )}
        <div className="grid2">
          <label className="field">
            <span className="label">Naam *</span>
            <input value={f.name} onChange={set("name")} />
          </label>
          <label className="field">
            <span className="label">Functie</span>
            <input value={f.jobTitle} onChange={set("jobTitle")} placeholder="bijv. planner, servicemonteur" />
          </label>
        </div>
        <DuplicateHint hits={dups} onUse={(d) => onSaved(d.id)} />
        <div className="grid3">
          <label className="field">
            <span className="label">E-mailadres</span>
            <input value={f.email} onChange={set("email")} type="email" autoComplete="off" />
          </label>
          <label className="field">
            <span className="label">Telefoon</span>
            <input value={f.phone} onChange={set("phone")} inputMode="tel" autoComplete="off" />
          </label>
          <label className="field">
            <span className="label">Mobiel</span>
            <input value={f.mobile} onChange={set("mobile")} inputMode="tel" autoComplete="off" />
          </label>
        </div>
        <label className="field">
          <span className="label">Bereikbaar</span>
          <input value={f.availability} onChange={set("availability")} placeholder="bijv. ma–vr 7–17 uur, of 24/7" />
        </label>
        <label className="field">
          <span className="label">Notities</span>
          <textarea value={f.notes} onChange={set("notes")} rows={3} />
        </label>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
